// VOYA9 morphing member dashboard. Single page that renders different
// "skins" based on memberState from /api/consumer/dashboard. Replaces the
// previous AppHome surface so a member always lands on a state-aware view.
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { FullPageLoader } from "@/components/Loader";
import CareTeamFloatingButton from "@/components/CareTeamFloatingButton";
import AlternativeSuggestionCard from "@/components/AlternativeSuggestionCard";
import ReattestationBanner from "@/components/ReattestationBanner";
import SafetyPauseBanner from "@/components/SafetyPauseBanner";
import VialStatus from "@/components/injection/VialStatus";
import { useAuth } from "@/lib/auth";
import { apiGet } from "@/lib/api";
import { getOnboarding, getProtocol, getHistory } from "@/lib/injectionApi";
import { useEvent, useResyncOnVisible } from "@/lib/realtime";
import { C, colorSchemeForProgram } from "@/lib/brand";
import { hasPendingRecommendation, isPendingRecommendation } from "@/lib/consultation";
import { fmtDate, fmtMoney, titleCase } from "@/lib/format";

// Low-frequency safety-net poll. Realtime is the primary signal; this
// catches WS-drop scenarios where reconnect didn't run for some reason.
const SAFETY_POLL_MS = 5 * 60 * 1000;

export default function MemberDashboard() {
  const { consumer } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // Injection onboarding + protocol live alongside the dashboard so we
  // can (a) auto-route first-time injectors into onboarding and
  // (b) render the toolkit card with live protocol + vial state.
  const [injection, setInjection] = useState({ onboarding: null, protocol: null });

  // Hits /api/consumer/dashboard. If that endpoint isn't live yet, falls
  // back to composing a state from /subscriptions + /consultations so a
  // logged-in user never sees a blank surface.
  const fetchDashboard = async () => {
    try {
      const d = await apiGet("/consumer/dashboard");
      setData(d || {});
      setError(false);
    } catch (err) {
      if (err.status === 404) {
        try {
          const [subs, consults, msgs] = await Promise.all([
            apiGet("/consumer/subscriptions").catch(() => []),
            apiGet("/consumer/consultations").catch(() => []),
            apiGet("/consumer/messages").catch(() => null),
          ]);
          setData(deriveFallback(subs || [], consults || [], msgs));
          setError(false);
        } catch {
          setError(true);
        }
      } else {
        setError(true);
      }
    }
  };

  useEffect(() => {
    fetchDashboard();
    // Safety-net poll only — realtime via useEvent below is the
    // primary signal. Visibility-change resync handled by
    // useResyncOnVisible.
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchDashboard();
    }, SAFETY_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ---- Realtime: react to physician-driven consultation transitions
  // (approved / reviewing / requested-info / etc.) without polling.
  // Filtered: we only refetch when the event's consultationId matches
  // the currently-rendered consultation, OR when no consultation is
  // currently shown (e.g., a brand-new claim arriving).
  useEvent("consultation:status_changed", (evt) => {
    const currentId = data?.currentConsultation?._id || data?.currentConsultation?.id;
    if (!currentId || evt.consultationId === currentId) {
      fetchDashboard();
    }
  });
  // New chat message could change unread badges + member state (e.g.,
  // physician_message_awaiting). Cheap to refetch.
  useEvent("message.new", () => fetchDashboard());
  // Audit appended doesn't change priority but may surface new
  // physicianRecommendation / reattestation / safetyPause data.
  useEvent("consultation:audit_appended", (evt) => {
    const currentId = data?.currentConsultation?._id || data?.currentConsultation?.id;
    if (!currentId || evt.consultationId === currentId) {
      fetchDashboard();
    }
  });
  useResyncOnVisible(fetchDashboard);

  // Once an active subscription is detected, pull injection onboarding +
  // protocol + a 1-entry history probe so we can (a) decide whether to
  // auto-route the member into the kit-arrival onboarding flow, (b)
  // light up the toolkit card on this page, and (c) detect the day-0
  // moment (active sub, kit landed, zero injections logged) so we can
  // celebrate the first injection rather than dropping members into a
  // generic "Hey {firstName}" hero.
  useEffect(() => {
    const hasActiveSub = !!data?.activeSubscription;
    if (!hasActiveSub) return;
    let cancelled = false;
    (async () => {
      const [ob, p, h] = await Promise.all([
        getOnboarding().catch(() => null),
        getProtocol().catch(() => null),
        getHistory({ limit: 1 }).catch(() => ({ entries: [] })),
      ]);
      if (!cancelled) {
        setInjection({
          onboarding: ob,
          protocol: p,
          hasLoggedFirstInjection: (h?.entries || []).length > 0,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [data?.activeSubscription?._id, data?.activeSubscription?.id]);

  // Auto-route first-time injectors into the 5-step onboarding flow.
  // Triggers exactly once per session: when the member lands on /app
  // with an active subscription AND onboarding has never been completed
  // AND they haven't tapped SKIP on it this session.
  useEffect(() => {
    if (!data?.activeSubscription) return;
    if (!injection.onboarding) return; // wait for BE response
    if (injection.onboarding.completedAt) return;
    try {
      if (sessionStorage.getItem("voya9.injectionOnboarding.deferred") === "1") return;
    } catch { /* sessionStorage may be unavailable */ }
    // Mark deferred immediately so a back-button navigation doesn't
    // ping-pong members between dashboard and onboarding.
    try { sessionStorage.setItem("voya9.injectionOnboarding.deferred", "1"); } catch { /* ignore */ }
    navigate("/app/onboarding/injection", { replace: false });
  }, [data?.activeSubscription, injection.onboarding, navigate]);

  // Preview hooks for visual QA. Must run unconditionally before any
  // early returns to satisfy rules-of-hooks.
  const preview = usePreviewBanners();
  const [annualDismissedAt, setAnnualDismissedAt] = useState(() => {
    try { return Number(localStorage.getItem("voya9.reattest.annual.dismissedAt")) || 0; } catch { return 0; }
  });

  if (data === null && !error) return <FullPageLoader />;

  const rawMemberState = (data && data.memberState) || "no_consultation";
  // Defensive client-side override. The backend's priority resolver
  // doesn't always emit `physician_recommendation_pending` /
  // `physician_message_awaiting` yet — in those cases we'd otherwise
  // double-render: a "Let's get started" no_consultation hero stacked
  // on top of the recommendation/message card. If the consultation
  // payload tells us there's an open recommendation or unread physician
  // message during pre-approval, force the right immersive hero so the
  // page reads as a single editorial moment rather than two competing
  // calls to action.
  const memberState = (() => {
    // Recommendation always wins — it's the highest-priority decision
    // the member can make. Detect it across every shape the backend has
    // shipped: nested on consultation, on actionRequired (legacy), or
    // top-level. Without this guard we'd render a "Let's get started"
    // hero alongside the recommendation card.
    //
    // We must *also* check that the recommendation is still pending —
    // after the member accepts/declines, the backend keeps the field on
    // the consultation with a status/decided marker. Without this check
    // the dashboard sticks on the recommendation hero forever, even
    // though the member already made the decision.
    const consultRec = data?.currentConsultation?.physicianRecommendation;
    const topRec = data?.physicianRecommendation;
    const actionRec = data?.actionRequired?.physicianRecommendation;
    const hasRecommendation =
      isPendingRecommendation(consultRec)
      || isPendingRecommendation(topRec)
      || isPendingRecommendation(actionRec)
      || (
        // Legacy backend shape — some versions only set actionRequired.kind
        // and don't nest the recommendation. We honor it only when the
        // consultation doesn't already report a decided recommendation.
        (data?.actionRequired?.kind === "recommendation" || data?.actionRequired?.kind === "physician_recommendation")
        && isPendingRecommendation(consultRec ?? { /* presence-only */ })
      );
    if (hasRecommendation) return "physician_recommendation_pending";

    // Pre-approval unread physician message → the dedicated soft-chat hero.
    const isPreApproval =
      !data?.activeSubscription &&
      ["submitted", "in_review", "submitted_awaiting_review", "under_review"].includes(data?.currentConsultation?.status);
    if (isPreApproval && (data?.unreadMessages || 0) > 0) return "physician_message_awaiting";

    // Day-0 celebration moment: member has an active subscription, their
    // kit has shipped (or landed), and they haven't logged their first
    // injection yet. Replace the generic "Hey {firstName}" steady-state
    // hero with an editorial "your kit just landed" moment. Only
    // promotes from steady-state (and active_check_in_due — both
    // assume they've been injecting; if they haven't, the prompt is
    // wrong). We require `injection.protocol` to be loaded so we
    // don't flash this on members who happen to be between programs.
    // `?preview=kit-landed` forces this for visual QA.
    const isDayZero =
      preview.previewKitLanded ||
      (!!data?.activeSubscription &&
        injection.hasLoggedFirstInjection === false &&
        !!injection.protocol);
    if (isDayZero && ["active_steady_state", "active_check_in_due"].includes(rawMemberState)) {
      return "active_kit_landed";
    }

    return rawMemberState;
  })();

  // Mode 1 — pre-selection surface. Historically this rendered
  // PreSelectionHome (category tiles + "let a physician recommend").
  // Post Feb-29 pivot: consumers land on the catalogue by default, so
  // any member without an active plan/consultation who reaches /app
  // gets redirected to /catalogue rather than seeing the old physician-
  // recommend flow. The old surface is still available at
  // /for-employers/programs for the employer-sponsored model — we just
  // don't drop unaligned consumer members into it.
  const isMode1 = [
    "no_consultation",
    "consultation_draft",
    "assessment_in_progress",
  ].includes(memberState);
  if (isMode1) {
    // Preserve a mid-flight assessment session by routing back to it
    // directly rather than dropping the member on /catalogue and losing
    // their draft. Otherwise: catalogue is home.
    const draftSlug = data?.inProgressAssessment?.programSlug;
    return <Navigate to={draftSlug ? `/app/assessment/${draftSlug}` : "/catalogue"} replace />;
  }

  const consultation = (data && data.currentConsultation) || null;
  const subscription = (data && data.activeSubscription) || null;
  // actionRequired is the legacy top-level field. The current backend
  // contract drives the same UX through currentConsultation.physicianQuestion
  // (structured follow-up), .physicianRecommendation (alt-program proposal),
  // and .deniedNote (rejection note). Derive a unified `action` from every
  // source so the ActionCard always has something to show when memberState
  // is info_requested / physician_recommendation_pending / denied.
  const rawAction = (data && data.actionRequired) || null;
  const physicianQuestion = consultation?.physicianQuestion || null;
  const physicianRecommendation = consultation?.physicianRecommendation || null;
  const deniedNote = consultation?.deniedNote || null;
  let action = rawAction;
  // Physician recommendation gets first crack — it's the highest-priority
  // decision the member can make. Home card is just a summary + CTA;
  // side-by-side comparison lives on the Plan tab. Once the member has
  // accepted / declined we skip this branch so the decided recommendation
  // doesn't keep nagging from the home surface.
  if (!action && physicianRecommendation && isPendingRecommendation(physicianRecommendation)) {
    const recName = physicianRecommendation.programName || "a different program";
    const recTier = physicianRecommendation.tierLabel || physicianRecommendation.protocolTier || "";
    const recPrice = physicianRecommendation.monthlyPrice
      ? `$${physicianRecommendation.monthlyPrice}/mo`
      : "";
    action = {
      kind: "recommendation",
      // Plain-text fallback (used by aria/SEO and any client that doesn't
      // render the rich node).
      message: physicianRecommendation.reasonText
        || `Your physician thinks ${[recName, recTier].filter(Boolean).join(" — ")} may fit better${recPrice ? ` (${recPrice})` : ""}.`,
      // Rich node — bolds the program + tier and uses a comma after the
      // tier name so the line reads as a single clean editorial sentence
      // ("Your physician thinks **Weight Management, Plus tier** may fit
      // better ($229/mo).") instead of a flat run-on.
      messageNode: physicianRecommendation.reasonText
        ? null
        : (
          <>
            Your physician thinks{" "}
            <strong style={{ fontStyle: "normal", fontWeight: 800, color: C.ink }}>
              {recName}{recTier ? `, ${recTier} tier` : ""}
            </strong>
            {" "}may fit better{recPrice ? ` (${recPrice})` : ""}.
          </>
        ),
      askedAt: physicianRecommendation.recommendedAt,
      cta: { label: "REVIEW & DECIDE →", to: "/app/plan" },
    };
  }
  if (!action && physicianQuestion && (physicianQuestion.text || physicianQuestion.message)) {
    action = {
      kind: "question",
      message: physicianQuestion.text || physicianQuestion.message,
      askedAt: physicianQuestion.askedAt,
      cta: { label: "VIEW & REPLY →", to: `/app/care-team/${consultation?.id || consultation?._id || ""}` },
    };
  }
  if (!action && deniedNote && (deniedNote.text || deniedNote.message)) {
    action = {
      kind: "denial",
      message: deniedNote.text || deniedNote.message,
      cta: deniedNote.alternativePath
        ? { label: "EXPLORE ALTERNATIVE →", to: deniedNote.alternativePath }
        : { label: "READ FULL NOTE →", to: `/app/care-team/${consultation?.id || consultation?._id || ""}` },
    };
  }
  const unreadMessages = (data && data.unreadMessages) || 0;
  const physician = consultation?.assignedPhysician || data?.physician || null;
  const firstName = titleCase(consumer?.firstName || "there");
  const pauseEvent = (data && (data.safetyPauseEvent || data.activeSafetyEvent)) || null;
  const universalCore = (data && data.universalCore) || null;
  const hasAlternative = !!(consultation && consultation.alternativeSuggestion);

  // Accepts every shape the BE may emit while the contract stabilizes:
  //   - reattestationRequired === "triggered"     → triggered variant
  //   - reattestationRequired === "annual"        → annual variant
  //   - reattestationRequired === true            → annual variant (default)
  //   - daysUntilExpiry <= 14                     → annual variant
  const computedReattest = (() => {
    const rr = universalCore?.reattestationRequired;
    if (rr === "triggered") return "triggered";
    if (rr === "annual" || rr === true) return "annual";
    if (typeof universalCore?.daysUntilExpiry === "number" && universalCore.daysUntilExpiry <= 14) return "annual";
    return null;
  })();

  // Preview hooks for visual QA (?preview=reattest-annual etc.) — see
  // helper at bottom of file. Returns { previewReattestVariant,
  // previewUniversalCore, previewPauseEvent } when params are present.
  const reattestVariant = preview.previewReattestVariant ?? computedReattest;
  const effectiveUniversalCore = preview.previewUniversalCore ?? universalCore;
  const effectivePauseEvent = preview.previewPauseEvent ?? pauseEvent;

  // Annual reattestation is dismissible for 24h via localStorage. The
  // triggered variant is never dismissible (it gates a $19 add flow).
  const annualDismissed = reattestVariant === "annual" && (Date.now() - annualDismissedAt) < 24 * 60 * 60 * 1000;
  const handleReattestDismiss = () => {
    const t = Date.now();
    try { localStorage.setItem("voya9.reattest.annual.dismissedAt", String(t)); } catch { /* ignore */ }
    setAnnualDismissedAt(t);
  };
  const showReattestBanner = reattestVariant && !(reattestVariant === "annual" && annualDismissed);

  return (
    <div data-testid="member-dashboard" style={{ background: C.cream, paddingBottom: 140 }}>
      <StatusHero
        state={memberState}
        firstName={firstName}
        consultation={consultation}
        subscription={subscription}
        physician={physician}
        unreadMessages={unreadMessages}
        ctaOverride={
          memberState === "active_kit_landed"
            ? {
                to: injection.onboarding?.completedAt
                  ? "/app/injection/log"
                  : "/app/onboarding/injection",
                label: injection.onboarding?.completedAt
                  ? "BEGIN YOUR FIRST INJECTION →"
                  : "WALK ME THROUGH IT →",
                variant: "peach",
              }
            : null
        }
      />

      {effectivePauseEvent && <SafetyPauseBanner pauseEvent={effectivePauseEvent} />}

      {showReattestBanner && (
        <ReattestationBanner
          universalCore={effectiveUniversalCore || { daysUntilExpiry: 7 }}
          variant={reattestVariant}
          onDismiss={reattestVariant === "annual" ? handleReattestDismiss : undefined}
        />
      )}

      {hasAlternative && (
        <AlternativeSuggestionCard consultation={consultation} onResolved={fetchDashboard} />
      )}

      {action && !hasAlternative && memberState !== "approved_awaiting_activation" && (
        <ActionCard action={action} consultation={consultation} physician={physician} />
      )}

      {subscription && (
        <QuickStats
          subscription={subscription}
          memberState={memberState}
          // Either field on the sub OR the legacy dosageInstructions
          // text on the consultation lights up the protocol affordance.
          hasProtocol={
            !!subscription.activeProtocol
            || !!subscription.hasProtocol
            || !!subscription.protocolApprovedAt
            || !!consultation?.dosageInstructions?.trim()
          }
          // Active-subscription panel adopts the program's color identity
          // (peach for Weight, sage for Recovery, ochre for Skin & Beauty,
          // …) so the dashboard reads as "your program" rather than as a
          // generic peach card.
          scheme={colorSchemeForProgram({
            slug: subscription.programSlug,
            color: subscription.brandColor,
            accentColor: subscription.accentColor,
            darkColor: subscription.darkColor,
          })}
        />
      )}

      {subscription && (
        <InjectionToolkitCard
          protocol={injection.protocol}
          onboardingComplete={!!injection.onboarding?.completedAt}
        />
      )}

      <RecentActivity events={(data && data.recentActivity) || []} />

      <CareTeamFloatingButton
        unreadCount={unreadMessages}
        consultationId={consultation?.id || consultation?._id}
      />
    </div>
  );
}

/* =============================================================
 *  StatusHero — switches visual treatment based on memberState
 * ============================================================= */
function StatusHero({ state, firstName, consultation, subscription, physician, unreadMessages, ctaOverride }) {
  const variant = STATUS_VARIANTS[state] || STATUS_VARIANTS.no_consultation;
  const physicianName = physicianDisplay(physician);
  // ctaOverride lets the caller swap the variant's static CTA at render
  // time — needed for `active_kit_landed`, where the CTA target depends
  // on whether the member has completed onboarding (log directly) or
  // not (walk them through it first).
  const cta = ctaOverride || variant.cta;
  // States that read as "your work is done — sit tight" should feel
  // editorial and immersive. They have no follow-on action card / quick
  // stats / activity feed, so we let the dark hero claim the full
  // viewport instead of revealing cream below the fold. The 100dvh keeps
  // it honest on mobile Safari where 100vh undercounts the URL bar.
  const isImmersive = ["submitted_awaiting_review", "under_review", "approved_awaiting_activation", "physician_message_awaiting", "physician_recommendation_pending"].includes(state);

  return (
    <section
      data-testid={`status-hero-${state}`}
      style={{
        background: variant.bg,
        color: variant.fg,
        padding: "72px 24px 96px",
        position: "relative",
        ...(isImmersive
          ? {
              minHeight: "calc(100dvh - 64px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }
          : null),
        // Diagonal cut on the section itself (rather than an overlay
        // triangle) so the bottom edge IS the diagonal — no flat sliver
        // can leak through. Variant's `cutDirection` controls slope:
        //   "left"  → bottom-right raised (slopes down to the left)
        //   "right" → bottom-left raised  (slopes down to the right)
        ...(variant.diagonalCut
          ? {
              clipPath: variant.cutDirection === "left"
                ? "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)"
                : "polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - 32px))",
            }
          : null),
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <Eyebrow color={variant.eyebrowColor} testId={`hero-eyebrow-${state}`}>
          {variant.pulse && (
            <span
              aria-hidden
              className="animate-pulse-dot"
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: variant.pulseColor,
                marginRight: 10,
                verticalAlign: "middle",
              }}
            />
          )}
          {typeof variant.eyebrow === "function"
            ? variant.eyebrow({ physicianName, unreadMessages, subscription })
            : variant.eyebrow}
        </Eyebrow>

        <Headline
          before={typeof variant.headlineBefore === "function" ? variant.headlineBefore({ firstName, physicianName }) : variant.headlineBefore}
          italic={typeof variant.headlineItalic === "function" ? variant.headlineItalic({ firstName, physicianName }) : variant.headlineItalic}
          color={variant.fg}
          italicColor={variant.italicColor}
          size={variant.headlineSize || 76}
          className="mt-6"
        />

        {variant.body && (
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.6, color: variant.bodyColor || variant.fg, opacity: variant.bodyOpacity ?? 0.78, maxWidth: 580 }}>
            {typeof variant.body === "function" ? variant.body({ consultation, subscription, physicianName }) : variant.body}
          </p>
        )}

        {variant.timeline && consultation && (
          <ReviewTimeline current={state} eta={consultation?.estimatedReviewBy} fg={variant.fg} accent={variant.italicColor} />
        )}

        {cta && (
          <div style={{ marginTop: 36 }}>
            <Link to={cta.to}>
              <Button
                variant={cta.variant || "peach"}
                size="lg"
                testId={`hero-cta-${state}`}
              >
                {cta.label}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

/* ----- Variant table (single source of truth for member-state visuals) ----- */
const STATUS_VARIANTS = {
  no_consultation: {
    bg: C.cream,
    fg: C.ink,
    italicColor: C.peachDeep,
    eyebrow: "★ WELCOME TO VOYA9",
    eyebrowColor: C.peachDeep,
    headlineBefore: "LET'S GET",
    headlineItalic: "started.",
    body: "5 minutes to tell us about you, then a real physician designs a plan tailored to your goals.",
    cta: { to: "/app/programs", label: "BEGIN YOUR ASSESSMENT →", variant: "primary" },
    diagonalCut: true,
    cutDirection: "right",
  },
  submitted_awaiting_review: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: ({ physicianName }) => physicianName ? `REVIEWING WITH ${physicianName.toUpperCase()}` : "REVIEWING YOUR APPLICATION",
    eyebrowColor: C.peach,
    headlineBefore: "YOUR PHYSICIAN",
    headlineItalic: "is on it.",
    body: ({ consultation }) =>
      consultation?.estimatedReviewBy
        ? `Usually within 24 hours · ETA ${fmtDate(consultation.estimatedReviewBy)}`
        : "Usually within 24 hours.",
    bodyColor: "rgba(250,246,240,0.7)",
    bodyOpacity: 1,
    timeline: true,
  },
  under_review: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: ({ physicianName }) => physicianName ? `${physicianName.toUpperCase()} IS REVIEWING` : "PHYSICIAN REVIEW IN PROGRESS",
    eyebrowColor: C.peach,
    headlineBefore: "ALMOST",
    headlineItalic: "there.",
    body: ({ consultation }) =>
      consultation?.estimatedReviewBy
        ? `ETA ${fmtDate(consultation.estimatedReviewBy)} · we'll email when it's ready.`
        : "We'll email you when your physician's plan is ready.",
    bodyColor: "rgba(250,246,240,0.72)",
    bodyOpacity: 1,
    timeline: true,
  },
  info_requested: {
    bg: C.peach,
    fg: C.ink,
    italicColor: C.peachDark,
    eyebrow: ({ physicianName }) => physicianName ? `★ ${physicianName.toUpperCase()} HAS A QUESTION` : "★ A QUESTION FROM YOUR PHYSICIAN",
    eyebrowColor: C.peachDark,
    headlineBefore: "QUICK",
    headlineItalic: "follow-up needed.",
    body: "Reply when you have a moment so we can keep your plan moving.",
    bodyColor: C.peachDark,
    bodyOpacity: 0.9,
    diagonalCut: true,
    cutDirection: "left",
  },
  approved_awaiting_activation: {
    bg: C.sage,
    fg: C.ink,
    italicColor: C.sageDark,
    eyebrow: "★ APPROVED · CHOOSE YOUR PROGRAM",
    eyebrowColor: C.sageDark,
    headlineBefore: ({ physicianName }) => physicianName ? `${physicianName.toUpperCase()} HAS` : "YOUR PHYSICIAN HAS",
    headlineItalic: "designed your protocol.",
    body: "Activate your subscription to start. You have 30 days before approval expires.",
    bodyColor: C.sageDark,
    bodyOpacity: 0.85,
    // Drop the member straight onto the Plan tab where the activation
    // form lives — no detour through Care Team Chat. The Plan tab's
    // approved section is now payment-focused (dosage details surface
    // only after the subscription goes active).
    cta: { to: "/app/plan", label: "ACTIVATE NOW →", variant: "primary" },
    diagonalCut: true,
    cutDirection: "right",
  },
  // Day-0 celebration moment. Replaces the generic steady-state hero
  // when a member has an active subscription, has a protocol on file,
  // and hasn't logged their first injection yet. The first injection
  // is the highest-emotion moment in the program — landing here with
  // a generic "Hey {firstName}" feels like a missed beat.
  active_kit_landed: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: "★ YOUR KIT JUST LANDED",
    eyebrowColor: C.peach,
    headlineBefore: "TIME TO BEGIN —",
    headlineItalic: "slowly.",
    body: "Your first injection is the one that matters most. We'll walk you through it — protocol card open, foundations one tap away.",
    bodyColor: "rgba(250,246,240,0.72)",
    bodyOpacity: 1,
    // CTA target is set dynamically by `ctaOverride` on StatusHero —
    // routes to onboarding when incomplete, straight to the logger
    // when the member has already finished the 5-step walkthrough.
    cta: { to: "/app/onboarding/injection", label: "WALK ME THROUGH IT →", variant: "peach" },
  },
  active_steady_state: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.sage,
    eyebrow: ({ subscription }) => subscription?.dayInProgram
      ? `ACTIVE · DAY ${subscription.dayInProgram} · LOOKING GOOD`
      : "ACTIVE · LOOKING GOOD",
    eyebrowColor: C.sage,
    headlineBefore: "HEY",
    headlineItalic: ({ firstName }) => `${firstName}.`,
    body: ({ subscription }) =>
      subscription?.productName ? `You're on ${subscription.productName}. Everything's running on schedule.` : "Everything's running on schedule.",
    bodyColor: "rgba(250,246,240,0.7)",
    bodyOpacity: 1,
  },
  active_check_in_due: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: "★ TIME FOR A QUICK CHECK-IN",
    eyebrowColor: C.peach,
    headlineBefore: "HOW ARE YOU",
    headlineItalic: ({ firstName }) => `feeling, ${firstName}?`,
    body: "It's been about 4 weeks. Your monthly check-in helps your physician fine-tune your plan. About 3 minutes.",
    bodyColor: "rgba(250,246,240,0.72)",
    bodyOpacity: 1,
  },
  active_unread_messages: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: ({ unreadMessages, physicianName }) => {
      const who = physicianName || "your care team";
      return `★ ${unreadMessages || "NEW"} ${unreadMessages === 1 ? "MESSAGE" : "MESSAGES"} FROM ${who.toUpperCase()}`;
    },
    eyebrowColor: C.peach,
    headlineBefore: "YOU HAVE",
    headlineItalic: "mail.",
    body: "Tap below to read what your physician sent.",
    bodyColor: "rgba(250,246,240,0.72)",
    bodyOpacity: 1,
  },
  // Pre-active companion to active_unread_messages. Used when the physician
  // posts a non-blocking note *before* approving the protocol (e.g., "Saw
  // your case, want to chat about goals before I finalize"). Different from
  // info_requested — that's a structured question that blocks approval.
  // Different from active_unread_messages — that assumes the member already
  // has an active subscription. This is the in-between: under review, with
  // a soft message waiting that the member can reply to without action.
  physician_message_awaiting: {
    bg: C.ink,
    fg: C.cream,
    italicColor: C.peach,
    pulse: true,
    pulseColor: C.peach,
    eyebrow: ({ physicianName, unreadMessages }) => {
      const who = physicianName ? physicianName.toUpperCase() : "YOUR PHYSICIAN";
      const n = unreadMessages || 1;
      return `★ ${who} SENT YOU A ${n === 1 ? "MESSAGE" : `${n} MESSAGES`}`;
    },
    eyebrowColor: C.peach,
    headlineBefore: ({ physicianName }) => physicianName ? `${physicianName.toUpperCase()} WANTS` : "YOUR PHYSICIAN WANTS",
    headlineItalic: "to chat.",
    body: "Reply when you have a moment — your review continues either way.",
    bodyColor: "rgba(250,246,240,0.72)",
    bodyOpacity: 1,
    timeline: true,
  },
  paused: {
    bg: C.bone,
    fg: C.ink,
    italicColor: C.peachDeep,
    eyebrow: "PAUSED · RESUME ANYTIME",
    eyebrowColor: C.warmGray,
    headlineBefore: "TAKE THE",
    headlineItalic: "time you need.",
    body: "Your protocol is on pause. Resume whenever you're ready — no questions, no penalty.",
    diagonalCut: true,
    cutDirection: "right",
  },
  denied: {
    bg: C.cream,
    fg: C.ink,
    italicColor: C.peachDeep,
    eyebrow: "APPLICATION RESPONSE",
    eyebrowColor: C.peachDeep,
    headlineBefore: ({ physicianName }) => physicianName ? `${physicianName.toUpperCase()} HAS` : "YOUR PHYSICIAN HAS",
    headlineItalic: "shared their thoughts.",
    body: "Read their full note below — they may have an alternative path that fits better.",
    diagonalCut: true,
    cutDirection: "left",
  },
};

/* =============================================================
 *  ActionCard — only renders when actionRequired is present
 * ============================================================= */
function ActionCard({ action, consultation, physician }) {
  const physicianName = physicianDisplay(physician);
  const initials = physicianInitials(physician);
  const ts = action.askedAt || action.createdAt;
  const ago = ts ? relativeTime(ts) : null;
  const consultationId = consultation?.id || consultation?._id || "";
  // Always guarantee a reply affordance — if backend didn't attach a CTA,
  // we still route to the care-team thread so members can respond.
  const cta = action.cta || {
    label: action.kind === "denial" ? "READ FULL NOTE →" : "VIEW & REPLY →",
    to: `/app/care-team/${consultationId}`,
  };

  return (
    <section data-testid="action-card-section" style={{ position: "relative", padding: "32px 24px 0", background: C.cream }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div
          data-testid="action-card"
          style={{
            background: C.pure,
            border: `1.5px solid ${C.ink}`,
            padding: 28,
            position: "relative",
          }}
        >
          {physicianName && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                aria-hidden
                style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: C.ink, color: C.peach,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 800, letterSpacing: "0.08em",
                }}
              >
                {initials}
              </span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em", color: C.ink }}>
                  {physicianName}
                </div>
                <div style={{ fontSize: 11, color: C.warmGray, letterSpacing: "0.05em" }}>
                  {action.kind === "question" ? "Asked you a question" : action.kind === "approval" ? "Plan ready to activate" : action.kind === "denial" ? "Shared their thoughts" : "Care team update"}
                  {ago && <> · {ago}</>}
                </div>
              </div>
            </div>
          )}

          {(action.messageNode || action.message) && (
            <p
              style={{
                marginTop: physicianName ? 18 : 0,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                fontSize: 22,
                lineHeight: 1.45,
                color: C.ink,
              }}
            >
              "{action.messageNode || action.message}"
            </p>
          )}

          <div style={{ marginTop: 22 }}>
            <Link to={cta.to}>
              <Button variant="primary" block testId="action-card-cta">
                {cta.label}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =============================================================
 *  QuickStats — only when there's an active subscription
 *  Adopts the active program's color scheme so the panel reads as
 *  "your Weight protocol" / "your Recovery protocol" rather than a
 *  generic peach card.
 * ============================================================= */
function QuickStats({ subscription, memberState, hasProtocol, scheme }) {
  const checkInOverdue = subscription.checkInOverdue || memberState === "active_check_in_due";
  const subId = subscription._id || subscription.id;
  // Route the strip's deep-link straight to the protocol view when one
  // exists, so members in active_steady_state land on the dosing details
  // in a single tap. Without a structured protocol we fall back to the
  // Plan tab where the dosageInstructions card / activation flow lives.
  const stripTo = hasProtocol && subId ? `/app/protocols/${subId}` : "/app/plan";
  // Scheme colors for the NEXT SHIPMENT tile. Falls back to the original
  // peach palette when no scheme arrives so legacy callers keep working.
  const shipBg = scheme?.background || C.peach;
  const shipFg = scheme?.textPrimary || C.ink;
  const shipMuted = scheme?.textSecondary || C.peachDark;
  return (
    <section data-testid="quick-stats" style={{ position: "relative", padding: "32px 24px 0", background: C.cream }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }} className="!grid-cols-1 md:!grid-cols-2">
          <div data-testid="qs-shipment" style={{ background: shipBg, color: shipMuted, padding: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: shipMuted }}>NEXT SHIPMENT</div>
            <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: shipFg }}>
              {subscription.nextShipmentDate ? fmtDate(subscription.nextShipmentDate) : "Scheduled soon"}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85, color: shipMuted }}>
              ships automatically {subscription.shippingMethod ? `· ${subscription.shippingMethod}` : ""}
            </div>
          </div>
          <Link to="/app/plan/checkin" style={{ textDecoration: "none" }} data-testid="qs-checkin-link">
            <div style={{ background: checkInOverdue ? C.coral : C.sage, color: checkInOverdue ? C.cream : C.sageDark, padding: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>
                CHECK-IN {checkInOverdue ? "OVERDUE" : "DUE"}
              </div>
              <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", color: checkInOverdue ? C.cream : C.ink }}>
                {subscription.nextCheckInDate ? fmtDate(subscription.nextCheckInDate) : "When ready"}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                3-min check-in · keeps your plan optimized
              </div>
            </div>
          </Link>
        </div>
        <div style={{ marginTop: 12, padding: "18px 22px", background: C.bone, color: C.warmGray, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, borderLeft: scheme?.background ? `4px solid ${scheme.background}` : "none" }}>
          <span>
            {subscription.productName} · {fmtMoney(subscription.monthlyPrice || 0)}/mo
          </span>
          <Link
            to={stripTo}
            data-testid="qs-plan-link"
            style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.ink, textDecoration: "underline" }}
          >
            {hasProtocol ? "VIEW PRESCRIBED PROTOCOL →" : "VIEW FULL PLAN →"}
          </Link>
        </div>
      </div>
    </section>
  );
}

/* =============================================================
 *  InjectionToolkitCard — surfaced on the home dashboard when an
 *  active subscription is present. Members shouldn't have to dig into
 *  Profile to find their protocol card / logger / history.
 * ============================================================= */
function InjectionToolkitCard({ protocol, onboardingComplete }) {
  const hasProtocol = !!(protocol && protocol.compound);
  const vial = protocol?.vial && protocol.vial.reconstitutedAt ? protocol.vial : null;

  return (
    <section
      data-testid="dashboard-injection-toolkit"
      style={{ position: "relative", padding: "32px 24px 0", background: C.cream }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            background: C.pure,
            border: `1.5px solid ${C.ink}`,
            padding: 24,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
                ★ INJECTION TOOLKIT
              </div>
              <div style={{ marginTop: 8, fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", color: C.ink }}>
                {hasProtocol
                  ? <>Your <span style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400 }}>{protocol.compound}</span> protocol</>
                  : <>Your <span style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400 }}>injection</span> toolkit</>}
              </div>
              {hasProtocol && (
                <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray }}>
                  {protocol.dose}{protocol.doseUnit ? ` ${protocol.doseUnit}` : "mg"} · {protocol.frequency || formatScheduleFreq(protocol.schedule)}
                </div>
              )}
            </div>
            {!onboardingComplete && (
              <Link to="/app/onboarding/injection" data-testid="dashboard-injection-finish-onboarding" style={{ textDecoration: "none" }}>
                <span style={{ display: "inline-block", padding: "6px 12px", fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", background: C.peach, color: C.peachDark }}>
                  ★ FINISH ONBOARDING →
                </span>
              </Link>
            )}
          </div>

          {vial && (
            <div style={{ marginTop: 18 }}>
              <VialStatus vial={vial} testId="dashboard-vial" />
            </div>
          )}

          <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
            {hasProtocol && (
              <Link to="/app/injection/log" data-testid="dashboard-injection-log-link" style={{ textDecoration: "none" }}>
                <Button variant="primary" size="md">+ LOG INJECTION</Button>
              </Link>
            )}
            <Link to="/app/reference/injections" data-testid="dashboard-injection-reference-link" style={{ textDecoration: "none" }}>
              <Button variant={hasProtocol ? "ghost" : "primary"} size="md">PROTOCOL CARD</Button>
            </Link>
            <Link to="/app/injection/history" data-testid="dashboard-injection-history-link" style={{ textDecoration: "none" }}>
              <Button variant="ghost" size="md">HISTORY</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatScheduleFreq(sched) {
  if (!sched) return "Per physician";
  if (sched.intervalDays === 1) return "Daily";
  if (sched.intervalDays === 7) return "Weekly";
  if (sched.intervalDays > 1) return `Every ${sched.intervalDays} days`;
  return "Per physician";
}

/* =============================================================
 *  ReviewTimeline — embedded in the pending hero
 * ============================================================= */
function ReviewTimeline({ current, eta, fg, accent }) {
  const steps = [
    { id: "submitted", label: "Assessment submitted", done: true },
    { id: "review", label: "Physician review", active: ["submitted_awaiting_review", "under_review"].includes(current) },
    { id: "plan", label: "Personalized protocol", done: false },
  ];
  return (
    <div data-testid="review-timeline" style={{ marginTop: 36, display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14 }} className="!grid-cols-1 md:!grid-cols-3">
      {steps.map((s) => (
        <div
          key={s.id}
          style={{
            padding: 18,
            background: s.active ? "rgba(216,139,92,0.12)" : "rgba(250,246,240,0.05)",
            borderLeft: `2px solid ${s.done ? C.sage : s.active ? accent : "rgba(250,246,240,0.18)"}`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: s.active ? accent : s.done ? C.sage : "rgba(250,246,240,0.45)" }}>
            {s.done ? "✓ DONE" : s.active ? "● IN PROGRESS" : "PENDING"}
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: fg, opacity: s.done || s.active ? 1 : 0.55 }}>
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/* =============================================================
 *  RecentActivity — last few care-relationship events
 * ============================================================= */
function RecentActivity({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <section data-testid="recent-activity" style={{ position: "relative", padding: "60px 24px 32px", background: C.cream }}>
      <TopWedge color={C.cream} dir="left" />
      <div style={{ maxWidth: 800, margin: "0 auto", position: "relative", zIndex: 2 }}>
        <Eyebrow>RECENT</Eyebrow>
        <div style={{ marginTop: 16, background: C.pure, border: `1px solid ${C.warmGray3}` }}>
          {events.slice(0, 6).map((e, i) => (
            <div key={e.id || i} style={{ display: "flex", justifyContent: "space-between", padding: "16px 22px", borderBottom: i < events.length - 1 && i < 5 ? `1px solid ${C.warmGray4}` : "none", fontSize: 14, gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: C.ink }}>{e.title || e.label || "Update"}</div>
                {e.detail && <div style={{ marginTop: 2, color: C.warmGray, fontSize: 13 }}>{e.detail}</div>}
              </div>
              <div style={{ color: C.warmGray, fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", flexShrink: 0 }}>
                {e.at ? relativeTime(e.at) : ""}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =============================================================
 *  Helpers
 * ============================================================= */
function physicianDisplay(p) {
  if (!p) return null;
  const last = p.lastName || p.surname;
  if (!last) return p.fullName || p.name || null;
  return `Dr. ${titleCase(last)}`;
}
function physicianInitials(p) {
  if (!p) return "MD";
  const f = (p.firstName || "").trim()[0];
  const l = (p.lastName || "").trim()[0];
  if (f && l) return `${f}${l}`.toUpperCase();
  return (p.initials || "MD").slice(0, 2).toUpperCase();
}
function relativeTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

// Defensive fallback when /api/consumer/dashboard 404s — derive a state from
// existing consumer endpoints so a returning member never sees a blank screen.
function deriveFallback(subs, consults, msgs) {
  const activeSub = subs.find((s) => ["active", "paused", "cancel_at_period_end", "payment_failed"].includes(s.status));
  const approved = consults.find((c) => c.status === "approved");
  const inReview = consults.find((c) => c.status === "in_review");
  const submitted = consults.find((c) => c.status === "submitted");
  const infoRequested = consults.find((c) => c.status === "info_requested" || c.status === "clarification_requested");
  const denied = consults.find((c) => c.status === "denied" || c.status === "declined");
  const unreadMessages = msgs?.unreadCount || 0;

  let memberState = "no_consultation";
  if (activeSub) {
    if (activeSub.status === "paused") memberState = "paused";
    else if (unreadMessages > 0) memberState = "active_unread_messages";
    else if (activeSub.checkInOverdue) memberState = "active_check_in_due";
    else memberState = "active_steady_state";
  } else if (infoRequested) memberState = "info_requested";
  else if (approved) memberState = "approved_awaiting_activation";
  else if (inReview) memberState = "under_review";
  else if (submitted) memberState = "submitted_awaiting_review";
  else if (denied) memberState = "denied";

  return {
    memberState,
    currentConsultation: infoRequested || approved || inReview || submitted || denied || null,
    activeSubscription: activeSub || null,
    actionRequired: null,
    unreadMessages,
    physician: (infoRequested || approved || inReview || submitted)?.assignedPhysician || null,
    recentActivity: [],
  };
}


// ---- Preview hooks: render banners by URL param while BE field-emission
// is still pending. Use cases for visual QA:
//   /app?preview=reattest-annual          → annual reminder (dismissible)
//   /app?preview=reattest-annual&days=0   → "today" copy
//   /app?preview=reattest-triggered       → safety review before adding a program
//   /app?preview=safety-pause-pending     → orange pending banner + modal
//   /app?preview=safety-pause-resumed     → sage resolved banner
//   /app?preview=safety-pause-adjusted    → sage adjusted banner
//   /app?preview=safety-pause-kept        → bone kept-paused banner
//   /app?preview=safety-pause-ended       → peach ended-with-refund banner
// Strip the param from the URL after read so reloads stay clean? No —
// the param is intentionally sticky so QA can A/B-screenshot states.
function usePreviewBanners() {
  const [params] = useSearchParams();
  const preview = params.get("preview");
  if (!preview) return { previewReattestVariant: null, previewUniversalCore: null, previewPauseEvent: null, previewKitLanded: false };

  if (preview === "reattest-annual") {
    const days = Number(params.get("days"));
    const daysUntilExpiry = Number.isFinite(days) ? days : 7;
    return {
      previewReattestVariant: "annual",
      previewUniversalCore: { reattestationRequired: "annual", daysUntilExpiry },
      previewPauseEvent: null,
      previewKitLanded: false,
    };
  }
  if (preview === "reattest-triggered") {
    return {
      previewReattestVariant: "triggered",
      previewUniversalCore: { reattestationRequired: "triggered" },
      previewPauseEvent: null,
      previewKitLanded: false,
    };
  }
  if (preview === "kit-landed") {
    // Forces the day-0 hero regardless of injection history. Only
    // takes effect when an active subscription is present — the
    // surrounding QuickStats + toolkit card need a real sub to render.
    return {
      previewReattestVariant: null,
      previewUniversalCore: null,
      previewPauseEvent: null,
      previewKitLanded: true,
    };
  }

  const pauseMap = {
    "safety-pause-pending": { resolutionState: "pending", pausedAt: new Date(Date.now() - 35 * 60 * 1000).toISOString(), triggerRule: "a possible blood-pressure concern", consultationId: "preview" },
    "safety-pause-resumed": { resolutionState: "resumed", consultationId: "preview" },
    "safety-pause-adjusted": { resolutionState: "resumed_with_adjustment", consultationId: "preview" },
    "safety-pause-kept": { resolutionState: "kept_paused", consultationId: "preview" },
    "safety-pause-ended": { resolutionState: "ended_with_refund", refundDisplay: "$49.00", consultationId: "preview" },
  };
  if (pauseMap[preview]) {
    return { previewReattestVariant: null, previewUniversalCore: null, previewPauseEvent: pauseMap[preview], previewKitLanded: false };
  }
  return { previewReattestVariant: null, previewUniversalCore: null, previewPauseEvent: null, previewKitLanded: false };
}
